import React, { useState, useRef, useEffect, FormEvent, ChangeEvent, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, TextField } from '@dopomogai/ui';
import { Send } from 'lucide-react';
import clsx from 'clsx';
import { renderIcon } from '@dopomogai/utils';
import type { ChatMessage } from '@dopomogai/types'; // UPDATED IMPORT

interface LiveChatTestbedProps {
  initialMessages?: ChatMessage[];
  onSendMessage: (messageText: string) => Promise<void>;
  isAgentReplying?: boolean;
  widgetPrimaryColor?: string;
  widgetHeaderColor?: string;
}

export const LiveChatTestbed: React.FC<LiveChatTestbedProps> = ({
  initialMessages = [],
  onSendMessage,
  isAgentReplying = false,
  widgetPrimaryColor = '#3B82F6',
  widgetHeaderColor = '#4B5563',
}) => {
  const { t } = useTranslation('agentBuilder');
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(scrollToBottom, [messages]);

  useEffect(() => {
    setMessages(initialMessages);
  }, [initialMessages]);


  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmedInput = inputText.trim();
    if (!trimmedInput || isAgentReplying) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      sender: 'user',
      text: trimmedInput,
      timestamp: Date.now(),
    };
    setMessages(prev => [...prev, userMessage]);
    setInputText('');

    await onSendMessage(trimmedInput);
  };

  // For the Send button, if widgetPrimaryColor is meant to override the default "primary" variant color,
  // the Button component itself would need to support this, e.g., via CSS variables or a color prop.
  // For now, relying on variant="primary" and className for structural styling.
  // If specific background color override is essential and not supported by Button,
  // a wrapper div or custom CSS classes tied to widgetPrimaryColor would be needed.

  return (
    <div className="flex flex-col h-[500px] max-h-[70vh] w-full max-w-lg mx-auto bg-white border border-gray-300 rounded-lg shadow-xl overflow-hidden">
      <div className="p-3 border-b border-gray-200 bg-slate-50">
        <h3 className="text-sm font-semibold text-gray-700 text-center">
          {t('step5.liveChat.headerTitle')}
        </h3>
      </div>

      <div className="flex-grow p-4 space-y-4 overflow-y-auto bg-slate-50">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={clsx('flex', {
              'justify-end': msg.sender === 'user',
              'justify-start': msg.sender === 'agent',
              'justify-center': msg.sender === 'system',
            })}
          >
            {msg.sender === 'agent' && (
              <div className="shrink-0 mr-2 self-end">
                {renderIcon('messageSquare', { size: 20, className: "text-slate-400"})}
              </div>
            )}
            <div
              className={clsx('max-w-[70%] py-2 px-3.5 rounded-xl shadow-sm break-words', {
                // Apply specific background color for user messages via style prop
                // Agent messages will use standard styling or could also be customized if needed
                'text-white': msg.sender === 'user',
                'bg-slate-200 text-slate-800': msg.sender === 'agent', // Default agent bubble
                'text-xs text-slate-500 italic text-center w-full': msg.sender === 'system',
              })}
              style={
                msg.sender === 'user' ? { backgroundColor: widgetPrimaryColor } :
                msg.sender === 'agent' ? { backgroundColor: widgetHeaderColor, color: '#FFFFFF' } : {}
              }
            >
              {msg.isSystemStatus && msg.systemAction === 'using_tool' && (
                <div className="flex items-center gap-1.5">
                  {renderIcon('briefcase', {size: 12, className: "opacity-70"})}
                  <span>{t('step5.liveChat.usingTool', { toolName: msg.systemDetail || 'Unknown Tool' })}</span>
                </div>
              )}
              {msg.isSystemStatus && msg.systemAction === 'consulting_kb' && (
                 <div className="flex items-center gap-1.5">
                  {renderIcon('filetext', {size: 12, className: "opacity-70"})}
                  <span>{t('step5.liveChat.consultingKb', { fileName: msg.systemDetail || 'Knowledge Base' })}</span>
                </div>
              )}
              {!msg.isSystemStatus && msg.text}
            </div>
            {msg.sender === 'user' && (
              <div className="shrink-0 ml-2 self-end">
                {renderIcon('user', { size: 20, className: "text-slate-400"})}
              </div>
            )}
          </div>
        ))}
        {isAgentReplying && (
          <div className="flex justify-start">
            <div className="shrink-0 mr-2 self-end">
              {renderIcon('messageSquare', { size: 20, className: "text-slate-400"})}
            </div>
            <div className="py-2 px-3.5 rounded-xl shadow-sm" style={{ backgroundColor: widgetHeaderColor, color: '#FFFFFF'}}>
              <div className="flex items-center space-x-1">
                <div className="h-1.5 w-1.5 bg-current rounded-full animate-bounce [animation-delay:-0.3s]" />
                <div className="h-1.5 w-1.5 bg-current rounded-full animate-bounce [animation-delay:-0.15s]" />
                <div className="h-1.5 w-1.5 bg-current rounded-full animate-bounce" />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="p-3 border-t border-gray-200 bg-white">
        <div className="flex items-center gap-2">
          <TextField
            value={inputText}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setInputText(e.target.value)}
            placeholder={t('step5.liveChat.inputPlaceholder')}
            // TextField's className prop applies to its root. Use inputClassName for the <input>
            containerClassName="flex-grow" 
            inputClassName="py-2.5"
            disabled={isAgentReplying}
            autoFocus
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter' && !e.shiftKey) handleSubmit(e as any as FormEvent); }}
          />
          <Button
            ariaLabel={t('step5.liveChat.sendButtonAriaLabel')}
            icon={<Send size={18} />}
            variant="primary"
            disabled={!inputText.trim() || isAgentReplying}
            // style prop removed. If Button needs dynamic color from widgetPrimaryColor,
            // it should support it via a dedicated prop (e.g., color) or CSS variables.
            // className="px-3.5 bg-blue-500 hover:bg-blue-600" // Example static styling if variant="primary" is not enough
            className="px-3.5" // Basic structural class
            onClick={handleSubmit}
          />
        </div>
      </form>
    </div>
  );
};